import React from "react";
import styled from "@emotion/styled";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useFormContext } from "react-hook-form";
import useAxios from "@libs/axios/useAxios";
import { FILE_URL } from "@libs/apiUrl";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1rem",
});

const Input = styled.input({
  display: "none",
});

const UploadButton = styled.label({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  position: "relative",
  cursor: "pointer",
  border: "1px solid #ebebeb",
  paddingTop: "100%", // 1:1 비율 유지
});

const ButtonContent = styled.div({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.4rem",
});

const RemainingText = styled.span({
  fontSize: "1.1rem",
  color: "#999",
});

const PreviewContainer = styled.div({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "1rem",
});

const ImagePreview = styled.div({
  position: "relative",
  width: "100%",
  paddingTop: "100%",
  border: "1px solid #ebebeb",
  overflow: "hidden",
});

const PreviewImage = styled.img({
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transform: "translate(-50%, -50%)",
});

const DeleteButton = styled.div({
  position: "absolute",
  top: 6,
  right: 8,
  cursor: "pointer",
});

type Props = {
  files: any;
  setFiles: (files: any) => void;
};

// 백엔드 업로드 한도(spring.servlet.multipart.max-file-size)와 같다.
const MAX_FILE_BYTES = 20 * 1024 * 1024;

const FileUploadInput = ({ files, setFiles }: Props) => {
  const theme = useTheme();
  const { setValue, watch } = useFormContext();
  const { axiosInstance } = useAxios();

  const uploadOne = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("files", file);
    const res = await axiosInstance.post(FILE_URL, formData);
    const url = res.data?.data?.[0]?.url;
    if (typeof url !== "string" || !url) {
      throw new Error("업로드 응답에 파일 주소가 없습니다.");
    }
    return url;
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    let selectedFilesArray = Array.from(selectedFiles);

    // 남은 슬롯만큼만 업로드
    const remaining = 10 - files.length;
    if (remaining <= 0) return;

    if (selectedFilesArray.length > remaining) {
      selectedFilesArray = selectedFilesArray.slice(0, remaining);
      alert(`남은 등록 가능 수(${remaining}개)만큼만 업로드합니다.`);
    }

    const tooLarge = selectedFilesArray.filter((file) => file.size > MAX_FILE_BYTES);
    const uploadable = selectedFilesArray.filter((file) => file.size <= MAX_FILE_BYTES);

    try {
      // 한 장씩 보낸다. 여러 장을 한 요청에 담으면 요청 크기 한도에 걸린다.
      // 실패한 사진을 base64(data URL)로 대신 저장하지 않는다. 이미지 원본이
      // DB에 쌓여 운영 DB가 멈춘 적이 있다(2026-10-07).
      const newFiles: string[] = [];
      let failed = 0;
      for (const file of uploadable) {
        try {
          newFiles.push(await uploadOne(file));
        } catch (error) {
          failed += 1;
          console.error("파일 업로드 실패:", file.name, error);
        }
      }

      if (newFiles.length > 0) {
        const updatedFiles = [...files, ...newFiles];
        setFiles(updatedFiles);
        setValue("image_list", updatedFiles);
      }

      const problems: string[] = [];
      if (tooLarge.length > 0) {
        problems.push(`20MB를 넘는 사진 ${tooLarge.length}장은 올리지 않았습니다.`);
      }
      if (failed > 0) {
        problems.push(`사진 ${failed}장을 업로드하지 못했습니다. 다시 시도해 주세요.`);
      }
      if (problems.length > 0) {
        alert(problems.join("\n"));
      }
    } finally {
      event.target.value = "";
    }
  };

  const deleteItem = async (index: number) => {
    const fileToDelete = files[index];

    const updatedFiles = files.filter(
      (_: any, fileIndex: number) => fileIndex !== index,
    );

    // 예전에 data URL로 저장된 이미지는 저장소에 파일이 없으므로 삭제 요청을 보내지 않는다.
    if (typeof fileToDelete === "string" && fileToDelete.startsWith("data:")) {
      setFiles(updatedFiles);
      setValue("image_list", updatedFiles);
      return;
    }

    try {
      const res = await axiosInstance.delete(FILE_URL, {
        data: { file_url_list: [fileToDelete] },
      });

      if (res.status === 200) {
        setFiles(updatedFiles);
        setValue("image_list", updatedFiles);
      } else {
        alert("파일 삭제에 실패했습니다.");
      }
    } catch (error) {
      console.error("파일 삭제 중 에러가 발생했습니다:", error);
    }
  };

  return (
    <Container>
      <PreviewContainer>
        <Input
          type="file"
          id="file-upload"
          accept="image/*"
          multiple
          onChange={handleFileChange}
        />
        {watch("image_list")?.length < 10 && (
          <UploadButton htmlFor="file-upload">
            <ButtonContent>
              <Image src={theme.image.plus_gray.default} />
              <RemainingText>
                {10 - (watch("image_list")?.length || 0)}장 추가 가능
              </RemainingText>
            </ButtonContent>
          </UploadButton>
        )}
        {watch("image_list")?.map((file: any, index: number) => (
          <ImagePreview key={index}>
            <PreviewImage src={file} alt={`Preview ${index}`} />

            <DeleteButton onClick={() => deleteItem(index)}>
              <Image src={theme.image.close_transparent.default} />
            </DeleteButton>
          </ImagePreview>
        ))}
      </PreviewContainer>
    </Container>
  );
};

export default FileUploadInput;
